import dayjs from 'dayjs';
import { TimelineNumberOfDaysReport } from '../constants/index.jsx';
import { resizeImageTimelinePreview } from '../../../utils/images';
import { getShortenMonths } from '../../../utils/ui';

/** Timeline charts */
export const GetTimelineReportItems = (items, selectedIndex) => {
  if (selectedIndex < 0 || selectedIndex > items.length - 1) return [];

  let ar = {
    temperature: {
      data: [],
      domain: [],
    },
    gdd: {
      data: [],
      domain: [],
    },
    dew: {
      data: [],
      domain: [],
    },
    dewDuration: {
      data: [],
      domain: [],
    },
    humidity: {
      data: [],
      domain: [],
    },
    ndvi: {
      data: [],
    },
    soilTemperature: {
      data: [],
      domain: [],
    },
    soilMoisture: {
      data: [],
      domain: [],
    },
    sensor: {
      name: '',
    },
    dates: null,
  };

  //domains
  const temperatureDomainsMax = items.map((d) => (d.temperatureMax ? Number(d.temperatureMax.toFixed(0)) : null));
  const temperatureDomainsMin = items.map((d) => (d.temperatureMin ? Number(d.temperatureMin.toFixed(0)) : null));
  const gddDomains = items.map((d) => (d.gdd.value ? Number(d.gdd.value.toFixed(0)) : null));
  const dewDomains = items.map((d) => (d.dew && d.dew.value ? parseFloat(d.dew.value) : null));
  const dewDurationDomains = items.map((d) => (d.dewDuration ? d.dewDuration : null));
  const soilTemperatureDomains = items.map((d) =>
    d.soilTemperature && d.soilTemperature.length > 0 && d.soilTemperature[0].avg
      ? Number(d.soilTemperature[0].avg).toFixed(0)
      : null,
  );

  const startIndex = Math.max(0, selectedIndex - (TimelineNumberOfDaysReport - 1));
  const list = items.slice(startIndex, selectedIndex + 1);
  list.map((item) => {
    const dateFormated = dayjs(item.date).format('D MMM');

    let soilMoistureValue = null;
    if (item.soilMoisture && item.soilMoisture.length > 0) {
      item.soilMoisture.map((item) => {
        if (item !== null && soilMoistureValue === null) {
          soilMoistureValue = item.avg;
        }
      });
    }

    let soilTemperatureValue = null;
    if (item.soilTemperature && item.soilTemperature.length > 0) {
      item.soilTemperature.map((item) => {
        if (item !== null && soilTemperatureValue === null) {
          soilTemperatureValue = item.avg;
        }
      });
    }

    ar.temperature.data.push({
      date: dateFormated,
      value: item.temperatureAvg ? item.temperatureAvg.toFixed(1) : null,
      area: [
        item.temperatureMin ? item.temperatureMin.toFixed(1) : null,
        item.temperatureMax ? item.temperatureMax.toFixed(1) : null,
      ],
    });
    ar.gdd.data.push({
      date: dateFormated,
      value: item.gdd && item.gdd.value !== null ? item.gdd.value.toFixed(0) : null,
    });
    ar.dew.data.push({
      date: dateFormated,
      value: item.dew ? parseFloat(item.dew.value) : null,
    });
    ar.dewDuration.data.push({
      date: dateFormated,
      value: item.dewDuration,
    });
    ar.humidity.data.push({
      date: dateFormated,
      value: item.humidity ? parseFloat(item.humidity.value) : null,
    });
    ar.ndvi.data.push({
      date: dateFormated,
      value: item.ndvi && item.ndvi.value !== 'No data' && item.ndvi.value ? item.ndvi.value.toFixed(2) : null,
      area: [
        item.ndvi && item.ndvi.p95 ? item.ndvi.p95.toFixed(2) : null,
        item.ndvi && item.ndvi.p5 ? item.ndvi.p5.toFixed(2) : null,
      ],
    });
    ar.soilTemperature.data.push({
      date: dateFormated,
      value: soilTemperatureValue,
    });
    ar.soilMoisture.data.push({
      date: dateFormated,
      value: soilMoistureValue,
    });
    ar.sensor.name = item.location ? item.location.name : null;
  });

  ar.temperature.domain = [Math.min(...temperatureDomainsMin), Math.max(...temperatureDomainsMax)];
  ar.gdd.domain = [Math.min(...gddDomains), Math.max(...gddDomains)];
  ar.dew.domain = [Math.min(...dewDomains), Math.max(...dewDomains)];
  ar.dewDuration.domain = [Math.min(...dewDurationDomains), Math.max(...dewDurationDomains)];
  ar.dates = [list[0].date, list[list.length - 1].date];
  ar.soilTemperature.domain = [Math.min(...soilTemperatureDomains), Math.max(...soilTemperatureDomains)];

  return ar;
};

const ParseTimeToMinutes = (t) => {
  if (!t) return null;
  const match = t.match(/(\d+):(\d+)\s*(AM|PM)?/i);
  if (!match) return null;
  let hours = parseInt(match[1], 10);
  const minutes = parseInt(match[2], 10);
  const period = match[3];
  if (period) {
    if (period.toUpperCase() === 'PM' && hours !== 12) hours += 12;
    if (period.toUpperCase() === 'AM' && hours === 12) hours = 0;
  }
  return hours * 60 + minutes;
};

const FindClosestByTime = (photos, time) => {
  if (!photos.length || !time) return undefined;
  const target = ParseTimeToMinutes(time);
  if (target === null) return undefined;
  let closest = null;
  let minDiff = Infinity;
  for (const p of photos) {
    const val = ParseTimeToMinutes(p.taken_at);
    if (val === null) continue;
    const diff = Math.abs(val - target);
    if (diff < minDiff) {
      minDiff = diff;
      closest = p;
    }
  }
  return closest;
};

/** Vegetation charts */
export const GetTimelineReportItemsVegetation = (items, selectedIndex, time) => {
  if (selectedIndex < 0 || selectedIndex > items.length - 1) return [];

  let ar = {
    ndvi: {
      data: [],
    },
    leafNdvi: {
      data: [],
    },
    leafArea: {
      data: [],
    },
    soilNdvi: {
      data: [],
    },
    sensor: {
      name: '',
    },
    dates: null,
  };

  const startIndex = Math.max(0, selectedIndex - (TimelineNumberOfDaysReport - 1));
  const list = items.slice(startIndex, selectedIndex + 1);

  list.map((item) => {
    const dateFormated = dayjs(item.date).format('D MMM');

    const meta = time ? FindClosestByTime(item.photos.ndvi, time) : item.photos.ndvi[0];
    const metaMask = time ? FindClosestByTime(item.photos.mask, time) : item.photos.ndvi[0];
    const ndvi = meta ? meta.meta.full_ndvi : null;
    const cropNdvi = meta ? meta.meta.crop_ndvi : null;
    const soilNdvi = meta ? meta.meta.soil_ndvi : null;
    const leafArea = metaMask ? metaMask.meta.ratio : null;

    if (ndvi) {
      ar.ndvi.data.push({
        date: dateFormated,
        value: ndvi && ndvi.avg !== 'No data' && ndvi.avg ? ndvi.avg.toFixed(2) : null,
        area: [ndvi && ndvi.p95 ? ndvi.p95.toFixed(2) : null, ndvi && ndvi.p5 ? ndvi.p5.toFixed(2) : null],
      });
    }
    if (cropNdvi) {
      ar.leafNdvi.data.push({
        date: dateFormated,
        value: cropNdvi && cropNdvi.avg ? cropNdvi.avg.toFixed(2) : null,
        area: [
          cropNdvi && cropNdvi.avg && cropNdvi.p95 ? cropNdvi.p95.toFixed(2) : null,
          cropNdvi && cropNdvi.avg && cropNdvi.p5 ? cropNdvi.p5.toFixed(2) : null,
        ],
      });
    }
    if (leafArea) {
      ar.leafArea.data.push({
        date: dateFormated,
        value: leafArea ? (leafArea * 100).toFixed(1) : null,
      });
    }
    if (soilNdvi) {
      ar.soilNdvi.data.push({
        date: dateFormated,
        value: soilNdvi && soilNdvi.avg ? soilNdvi.avg.toFixed(2) : null,
        area: [
          soilNdvi && soilNdvi.avg && soilNdvi.p95 ? soilNdvi.p95.toFixed(2) : null,
          soilNdvi && soilNdvi.avg && soilNdvi.p5 ? soilNdvi.p5.toFixed(2) : null,
        ],
      });
    }

    ar.sensor.name = item.location ? item.location.name : null;
  });

  ar.dates = [list[0].date, list[list.length - 1].date];

  return ar;
};

const MergeArraysByDate = (arr1, arr2) => {
  const map = new Map();

  for (const item of arr1) {
    map.set(item.date, { ...item });
  }

  for (const item of arr2) {
    if (map.has(item.date)) {
      const existing = map.get(item.date);
      for (const key in item) {
        if (key !== 'date') {
          existing[key + '2'] = item[key];
        }
      }
    } else {
      const newItem = { date: item.date };
      for (const key in item) {
        if (key !== 'date') {
          newItem[key + '2'] = item[key];
        }
      }
      map.set(item.date, newItem);
    }
  }

  return Array.from(map.values());
};

/** Metrics (compared) */
export const MergeReportArrays = (arr1, arr2) => {
  if (!arr1 || !arr2 || arr1.length < 1 || arr2.length < 1) return null;
  const { temperature, gdd, dew, dewDuration, humidity, ndvi } = arr1;
  const { temperature2, gdd2, dew2, dewDuration2, humidity2, ndvi2 } = {
    temperature2: arr2.temperature,
    gdd2: arr2.gdd,
    dew2: arr2.dew,
    dewDuration2: arr2.dewDuration,
    humidity2: arr2.humidity,
    ndvi2: arr2.ndvi,
  };
  return {
    temperature: {
      data: MergeArraysByDate(temperature.data, temperature2.data),
      domain: [
        Math.min(...temperature.domain, ...temperature2.domain),
        Math.max(...temperature.domain, ...temperature2.domain),
      ],
    },
    gdd: {
      data: MergeArraysByDate(gdd.data, gdd2.data),
      domain: [Math.min(...gdd.domain, ...gdd2.domain), Math.max(...gdd.domain, ...gdd2.domain)],
    },
    dew: {
      data: MergeArraysByDate(dew.data, dew2.data),
      domain: [Math.min(...dew.domain, ...dew2.domain), Math.max(...dew.domain, ...dew2.domain)],
    },
    dewDuration: {
      data: MergeArraysByDate(dewDuration.data, dewDuration2.data),
      domain: [
        Math.min(...dewDuration.domain, ...dewDuration2.domain),
        Math.max(...dewDuration.domain, ...dewDuration2.domain),
      ],
    },
    humidity: {
      data: MergeArraysByDate(humidity.data, humidity2.data),
    },
    ndvi: {
      data: MergeArraysByDate(ndvi.data, ndvi2.data),
    },
    sensor: {
      name: arr1.sensor.name,
      name2: arr2.sensor.name,
    },
    dates: arr1.dates,
  };
};

/** Vegetation metrics (compared) */
export const MergeReportArraysVegetation = (arr1, arr2) => {
  if (!arr1 || !arr2 || arr1.length < 1 || arr2.length < 1) return null;
  const { ndvi, leafNdvi, soilNdvi, leafArea } = arr1;
  const { ndvi2, leafNdvi2, soilNdvi2, leafArea2 } = {
    ndvi2: arr2.ndvi,
    leafNdvi2: arr2.leafNdvi,
    soilNdvi2: arr2.soilNdvi,
    leafArea2: arr2.leafArea,
  };
  return {
    leafNdvi: {
      data: MergeArraysByDate(leafNdvi.data, leafNdvi2.data),
    },
    leafArea: {
      data: MergeArraysByDate(leafArea.data, leafArea2.data),
    },
    soilNdvi: {
      data: MergeArraysByDate(soilNdvi.data, soilNdvi2.data),
    },
    ndvi: {
      data: MergeArraysByDate(ndvi.data, ndvi2.data),
    },
    sensor: {
      name: arr1.sensor.name,
      name2: arr2.sensor.name,
    },
    dates: arr1.dates,
  };
};

export const GetReportTitle = ({ t, startDate, endDate }) => {
  const end = dayjs(endDate).format('D MMM');
  let start;
  if (dayjs(startDate).month() === dayjs(endDate).month()) {
    start = dayjs(startDate).format('D');
  } else start = dayjs(startDate).format('D MMM');
  return `${t('sensor.summary')} ${start} – ${end}`;
};

/**
 * Mutation sensor for showing data in timeline image slider
 * @param  {Array} dates set of available dates
 * @param  {Object} imagesSelected set of the first sensor images with dates as keys and image urls as values
 * @param  {Object} imagesCompared set of the second sensor images with dates as keys and image urls as values
 * @param  {Object} photosType photos type
 * @return {Array} images set of accurate image date, month, thumbnail image, thumbnail compared image, disabled state
 */
export const GetSliderImages = ({ t, dates, imagesSelected, imagesCompared, photosType }) => {
  if (!dates || !photosType) return [];

  const Months = getShortenMonths(t);

  let allDates = FillMissingDates([...dates]);
  let thumbnails = [];
  let thumbnailsCompared = [];

  if (imagesSelected && imagesSelected.length > 0) {
    thumbnails = imagesSelected.reduce((acc, { date, photos }) => {
      const imgDay = photos.day.length ? photos.day.at(-1) : null;
      const imgNight = photos.night.length ? photos.night.at(-1) : null;
      acc[date] = imgDay || imgNight ? resizeImageTimelinePreview(imgDay?.url || imgNight?.url) : '';
      return acc;
    }, {});
  }

  if (imagesCompared && imagesCompared.length > 0) {
    thumbnailsCompared = imagesCompared.reduce((acc, { date, photos }) => {
      const imgDay = photos.day.length ? photos.day.at(-1) : null;
      const imgNight = photos.night.length ? photos.night.at(-1) : null;
      acc[date] = imgDay || imgNight ? resizeImageTimelinePreview(imgDay?.url || imgNight?.url) : '';
      return acc;
    }, {});
  }

  const { images } = allDates.reduce(
    (acc, date) => {
      let dateMonth = Months[dayjs(date).month()];

      if (dateMonth !== acc.month) {
        acc.month = dateMonth;
      } else {
        dateMonth = '';
      }
      const thumbnail = thumbnails[date];
      const thumbnailCompared = thumbnailsCompared[date];

      acc.images.push({
        date,
        dateMonth,
        thumbnail: thumbnail ?? '',
        thumbnailCompared: thumbnailCompared ?? '',
        disabled: thumbnail === undefined && thumbnailCompared === undefined,
      });
      return acc;
    },
    { images: [], month: '' },
  );

  return images;
};

const FillMissingDates = (dates) => {
  const parsedDates = dates.map((date) => new Date(date));
  parsedDates.sort((a, b) => a - b);

  const startDate = new Date(parsedDates[0]);
  const endDate = new Date(parsedDates[parsedDates.length - 1]);

  const allDates = [];
  const currentDate = new Date(startDate);

  while (currentDate <= endDate) {
    allDates.push(new Date(currentDate)); // add copy
    currentDate.setDate(currentDate.getDate() + 1); // next day
  }

  const formattedAllDates = allDates.map((date) => date.toISOString().split('T')[0]);

  const existingDateStrings = new Set(parsedDates.map((date) => date.toISOString().split('T')[0]));

  const missingDates = formattedAllDates.filter((dateStr) => !existingDateStrings.has(dateStr));

  return [...dates, ...missingDates].sort();
};
